/**
 * Cozzydine Storage Wrapper & Server Syncer
 * Implements transparent interceptors on the window.localStorage object to automatically
 * save, retrieve, and backup all customized properties, images, rooms lists, and bookings
 * to the Node/Express backend container permanently. Bypasses browser quota restrictions (QuotaExceededError)
 * by storing elements in a memory-backed cache if the browser quota is ever exceeded.
 */

// Global state variables for tracking sync phase
declare global {
  interface Window {
    __CozzydineSyncing?: boolean;
    __CozzydineMemoryCache?: Record<string, string>;
  }
}

// Memory cache fallback dictionary
if (typeof window !== 'undefined') {
  if (!window.__CozzydineMemoryCache) {
    window.__CozzydineMemoryCache = {};
  }

  // Safe robust checker for sandbox-related security exceptions or insecure operations
  const isSecurityErrorCheck = (err: any): boolean => {
    if (!err) return false;
    const msg = String(err.message || err.description || err).toLowerCase();
    const name = String(err.name || '').toLowerCase();
    return (
      msg.includes('insecure') || 
      msg.includes('securityerror') || 
      msg.includes('websocket') ||
      name.includes('securityerror') ||
      name.includes('insecure')
    );
  };

  // Prevent browser's default behavior for SecurityError "The operation is insecure" or Vite WebSocket failures inside sandboxed environment
  window.addEventListener('error', (event) => {
    const isSec = isSecurityErrorCheck(event.error) || isSecurityErrorCheck(event.message);
    if (isSec) {
      event.preventDefault();
      event.stopPropagation();
      console.warn('[Cozzydine Sandbox] Suppressed sandboxed environment error:', event.error || event.message);
    }
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    const isSec = isSecurityErrorCheck(event.reason);
    if (isSec) {
      event.preventDefault();
      event.stopPropagation();
      console.warn('[Cozzydine Sandbox] Suppressed sandboxed environment unhandled promise rejection:', event.reason);
    }
  }, true);

  // Wrap window.WebSocket to gracefully catch and return a safe mock socket if the browser blocks WebSocket creation in sandboxed frames
  try {
    const OriginalWebSocket = window.WebSocket;
    if (OriginalWebSocket) {
      class SafeWebSocket {
        constructor(url: string | URL, protocols?: string | string[]) {
          try {
            return new OriginalWebSocket(url, protocols);
          } catch (e: any) {
            console.warn('[Cozzydine Sandbox] WebSocket instantiation blocked (SecurityError: The operation is insecure). Returning mock socket.', e);
            const mockSocket: any = {
              url: String(url),
              readyState: 3, // CLOSED
              bufferedAmount: 0,
              extensions: '',
              protocol: '',
              binaryType: 'blob',
              onopen: null,
              onmessage: null,
              onerror: null,
              onclose: null,
              send() {},
              close() {},
              addEventListener() {},
              removeEventListener() {},
              dispatchEvent() { return true; }
            };
            return mockSocket;
          }
        }
      }

      // Copy all static properties and methods of WebSocket
      Object.getOwnPropertyNames(OriginalWebSocket).forEach((key) => {
        if (key !== 'prototype' && key !== 'name' && key !== 'length') {
          try {
            (SafeWebSocket as any)[key] = (OriginalWebSocket as any)[key];
          } catch (e) {}
        }
      });

      SafeWebSocket.prototype = OriginalWebSocket.prototype;
      window.WebSocket = SafeWebSocket as any;
    }
  } catch (err) {
    console.warn('[Cozzydine Sandbox] Could not patch window.WebSocket:', err);
  }
}

// Helper to check if localStorage is truly available and secure (cached to avoid redundant SecurityError logs)
let isLocalStorageSupported: boolean | null = null;
let cachedStorage: Storage | null = null;

const getLocalStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  if (isLocalStorageSupported !== null) {
    return isLocalStorageSupported ? cachedStorage : null;
  }
  try {
    const storage = window.localStorage;
    if (!storage) {
      isLocalStorageSupported = false;
      return null;
    }
    const testKey = '__cozzydine_storage_test__';
    storage.setItem(testKey, testKey);
    storage.removeItem(testKey);
    isLocalStorageSupported = true;
    cachedStorage = storage;
    return storage;
  } catch (e) {
    isLocalStorageSupported = false;
    cachedStorage = null;
    return null;
  }
};

const nativeGetItem = typeof window !== 'undefined' ? Storage.prototype.getItem : null;
const nativeSetItem = typeof window !== 'undefined' ? Storage.prototype.setItem : null;
const nativeRemoveItem = typeof window !== 'undefined' ? Storage.prototype.removeItem : null;
const nativeClear = typeof window !== 'undefined' ? Storage.prototype.clear : null;
const nativeKey = typeof window !== 'undefined' ? Storage.prototype.key : null;

/**
 * Highly resilient secure-wrapped local storage helper.
 * Guaranteed never to throw "The operation is insecure" or quota exceptions.
 */
export const safeLocalStorage = {
  getItem(key: string): string | null {
    try {
      const storage = getLocalStorage();
      if (storage && nativeGetItem) {
        return nativeGetItem.call(storage, key);
      }
    } catch (e) {}
    if (typeof window !== 'undefined' && window.__CozzydineMemoryCache) {
      return window.__CozzydineMemoryCache[key] || null;
    }
    return null;
  },

  setItem(key: string, value: string): void {
    if (typeof window !== 'undefined') {
      if (!window.__CozzydineMemoryCache) {
        window.__CozzydineMemoryCache = {};
      }
      window.__CozzydineMemoryCache[key] = value;
    }

    try {
      const storage = getLocalStorage();
      if (storage && nativeSetItem) {
        nativeSetItem.call(storage, key, value);
      }
    } catch (err) {
      console.warn(`[Cozzydine Storage] LocalStorage setItem failed for key "${key}":`, err);
    }

    // Sync to server-side JSON store silently if not currently performing hydration,
    // and only for cozzydine configurations/images/bookings
    if (typeof window !== 'undefined' && !window.__CozzydineSyncing && key.startsWith('cozzydine_')) {
      fetch('/api/store', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ key, value }),
      }).catch((err) => {
        console.error('[Cozzydine Storage] Failed to send key-value sync data to server:', err);
      });
    }
  },

  removeItem(key: string): void {
    if (typeof window !== 'undefined' && window.__CozzydineMemoryCache) {
      delete window.__CozzydineMemoryCache[key];
    }

    try {
      const storage = getLocalStorage();
      if (storage && nativeRemoveItem) {
        nativeRemoveItem.call(storage, key);
      }
    } catch (err) {}

    if (typeof window !== 'undefined' && !window.__CozzydineSyncing && key.startsWith('cozzydine_')) {
      fetch('/api/store', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ key, value: null }),
      }).catch((err) => {
        console.error('[Cozzydine Storage] Failed to send deletion sync key-value to server:', err);
      });
    }
  },

  clear(): void {
    const keysToSync = Object.keys(typeof window !== 'undefined' ? (window.__CozzydineMemoryCache || {}) : {}).filter(k => k.startsWith('cozzydine_'));
    
    if (typeof window !== 'undefined') {
      window.__CozzydineMemoryCache = {};
    }

    try {
      const storage = getLocalStorage();
      if (storage && nativeClear) {
        nativeClear.call(storage);
      }
    } catch (err) {}

    if (typeof window !== 'undefined' && !window.__CozzydineSyncing) {
      // Send bulk deletion to server
      const batchData: Record<string, null> = {};
      keysToSync.forEach(k => {
        batchData[k] = null;
      });

      if (keysToSync.length > 0) {
        fetch('/api/store', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ batch: batchData }),
        }).catch((err) => {
          console.error('[Cozzydine Storage] Failed to send bulk clearance sync to server:', err);
        });
      }
    }
  },

  key(index: number): string | null {
    try {
      const storage = getLocalStorage();
      if (storage && nativeKey) {
        return nativeKey.call(storage, index);
      }
    } catch (e) {}
    if (typeof window !== 'undefined' && window.__CozzydineMemoryCache) {
      return Object.keys(window.__CozzydineMemoryCache)[index] || null;
    }
    return null;
  },

  get length(): number {
    try {
      const storage = getLocalStorage();
      if (storage) {
        return storage.length;
      }
    } catch (e) {}
    if (typeof window !== 'undefined' && window.__CozzydineMemoryCache) {
      return Object.keys(window.__CozzydineMemoryCache).length;
    }
    return 0;
  }
};

// Safe transparent proxy override for the global window.localStorage where possible to catch any stray library access
if (typeof window !== 'undefined') {
  try {
    const testStorage = getLocalStorage();
    if (testStorage) {
      window.localStorage.setItem = function (key: string, value: string): void {
        safeLocalStorage.setItem(key, value);
      };
      window.localStorage.getItem = function (key: string): string | null {
        return safeLocalStorage.getItem(key);
      };
      window.localStorage.removeItem = function (key: string): void {
        safeLocalStorage.removeItem(key);
      };
      window.localStorage.clear = function (): void {
        safeLocalStorage.clear();
      };
    }
  } catch (err) {
    // If browser blocks accessing/overwriting window.localStorage completely, we swallow this gracefully
    console.warn('[Cozzydine Storage] Could not patch global window.localStorage directly (this is normal in strict sandbox mode). Using secure custom exports instead.', err);
  }
}

/**
 * Loads entire server-side store data, and synchronizes it into client-side
 * storage seamlessly without triggering circular network writes.
 */
export async function hydrateStorageFromServer(): Promise<boolean> {
  if (typeof window === 'undefined') return false;

  try {
    let serverData: any = null;
    let fromStatic = false;

    // 1. Try fetching from dynamic /api/store endpoint
    try {
      const apiRes = await fetch('/api/store');
      if (apiRes && apiRes.ok) {
        const contentType = apiRes.headers.get('content-type') || '';
        if (contentType.includes('application/json')) {
          serverData = await apiRes.json();
        } else {
          console.warn('[Cozzydine Storage] /api/store responded with non-JSON format (suggesting custom routing or fallback to index.html):', contentType);
        }
      }
    } catch (apiErr) {
      console.warn('[Cozzydine Storage] Remote backend database is missing or unreachable:', apiErr);
    }

    // 2. Fall back to /cozzydine_store.json if the remote API failed or returned non-JSON
    if (!serverData) {
      const busterUrl = `/cozzydine_store.json?t=${Date.now()}`;
      console.log(`[Cozzydine Storage] Attempting to pull bundled configurations from: ${busterUrl}`);
      try {
        const staticRes = await fetch(busterUrl, { cache: 'no-store' });
        if (staticRes && staticRes.ok) {
          try {
            const staticData = await staticRes.json();
            if (staticData && typeof staticData === 'object' && !Array.isArray(staticData)) {
              // Verify at least one cozzydine key is present to guarantee we successfully resolved the backup, and not a fallback HTML page
              const hasCozzydineKeys = Object.keys(staticData).some(key => key.startsWith('cozzydine_'));
              if (hasCozzydineKeys) {
                serverData = staticData;
                fromStatic = true;
                console.log('[Cozzydine Storage] Valid JSON backup parsed successfully from root endpoint.');
              }
            }
          } catch (jsonErr) {
            console.warn('[Cozzydine Storage] Object from /cozzydine_store.json could not be parsed as JSON, likely SPA HTML rewrite fallback:', jsonErr);
          }
        }
      } catch (staticErr) {
        console.warn('[Cozzydine Storage] Static fallback file fetch failed entirely:', staticErr);
      }
    }

    // 3. Fall back further to relative ./cozzydine_store.json if root /cozzydine_store.json failed or returned non-JSON
    if (!serverData) {
      const relativeBusterUrl = `./cozzydine_store.json?t=${Date.now()}`;
      try {
        const publicRes = await fetch(relativeBusterUrl, { cache: 'no-store' });
        if (publicRes && publicRes.ok) {
          try {
            const publicData = await publicRes.json();
            if (publicData && typeof publicData === 'object' && !Array.isArray(publicData)) {
              const hasCozzydineKeys = Object.keys(publicData).some(key => key.startsWith('cozzydine_'));
              if (hasCozzydineKeys) {
                serverData = publicData;
                fromStatic = true;
                console.log('[Cozzydine Storage] Valid JSON backup parsed successfully from relative path.');
              }
            }
          } catch (jsonErr) {
            console.warn('[Cozzydine Storage] Object from relative cozzydine_store.json could not be parsed as JSON:', jsonErr);
          }
        }
      } catch (pe) {
        console.warn('[Cozzydine Storage] Web root relative fallback check failed:', pe);
      }
    }

    if (!serverData || typeof serverData !== 'object') {
      console.warn('[Cozzydine Storage] No custom parameters, booking systems, or customized room photos loaded (falling back to pristine default assets).');
      return false;
    }

    // Apply synchronized keys to localStorage and caching
    window.__CozzydineSyncing = true;
    if (!window.__CozzydineMemoryCache) {
      window.__CozzydineMemoryCache = {};
    }

    console.log(`[Cozzydine Storage] Successfully resolved parameters from ${fromStatic ? 'Uploaded GitHub/Cloudflare static file' : 'Remote live server'}:`, Object.keys(serverData).length);

    for (const [key, val] of Object.entries(serverData)) {
      if (val === null || val === undefined) {
        delete window.__CozzydineMemoryCache[key];
        try {
          safeLocalStorage.removeItem(key);
        } catch (e) {}
      } else {
        const stringVal = String(val);
        window.__CozzydineMemoryCache[key] = stringVal;
        try {
          safeLocalStorage.setItem(key, stringVal);
        } catch (e) {
          // Even if local storage fails due to quota limits, keeping it in memoryCache ensures
          // that the app retains the custom images/data for the session!
        }
      }
    }

    window.__CozzydineSyncing = false;
    return true;
  } catch (err) {
    console.error('[Cozzydine Storage] Severe exception during database synchronization:', err);
  } finally {
    window.__CozzydineSyncing = false;
  }
  return false;
}
